import { useCallback, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { PayrollView } from '../../types/entity-types';

export type PayrollAction =
  | 'PAYROLL_SETUP'
  | 'MANUAL_SELECT_BENEFICIARIES'
  | 'MANUAL_ADD_EMPLOYEES'
  | 'FINANCIAL_INFORMATION'
  | 'PAYROLL_SUMMARY'
  | 'REVIEW_AND_PAY'
  | 'PAYROLL_PAYMENT_SUCCESS'
  | 'COMPLETED_PAYROLL_SUMMARY';

export type EmployeeDrawerMode = 'ADD' | 'EDIT' | null;

export const usePayrollFlow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [payrollAction, setPayrollAction] = useState<PayrollAction | null>(null);
  const [isEmployeeDrawerOpen, setIsEmployeeDrawerOpen] = useState(false);
  const [employeeDrawerMode, setEmployeeDrawerMode] = useState<EmployeeDrawerMode>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [currentPayroll, setCurrentPayroll] = useState<PayrollView | null>(null);
  const [copiedPayroll, setCopiedPayroll] = useState<PayrollView | null>(null);

  const updatePayrollAction = (action: PayrollAction) => {
    setPayrollAction(action);
  };

  const openEmployeeDrawer = (mode: EmployeeDrawerMode, employeeId?: string) => {
    setIsEmployeeDrawerOpen(true);
    setEmployeeDrawerMode(mode);
    setSelectedEmployeeId(employeeId || null);
  };

  const closeEmployeeDrawer = () => {
    setIsEmployeeDrawerOpen(false);
    setEmployeeDrawerMode(null);
    setSelectedEmployeeId(null);
  };

  const viewExistingPayroll = (payroll: PayrollView) => {
    setCurrentPayroll(payroll);

    switch (payroll.status) {
      case 'IN_DRAFT':
        if (payroll.records.length > 0) {
          setPayrollAction('FINANCIAL_INFORMATION');
        } else {
          setPayrollAction('MANUAL_ADD_EMPLOYEES');
        }
        break;
      case 'WAITING_FOR_APPROVAL':
        setPayrollAction('PAYROLL_SUMMARY');
        break;
      case 'APPROVED':
        setPayrollAction('REVIEW_AND_PAY');
        break;
      case 'SUCCEEDED':
        setPayrollAction('COMPLETED_PAYROLL_SUMMARY');
        break;
    }
  };

  const clearPayrollState = () => {
    setPayrollAction(null);
    setIsEmployeeDrawerOpen(false);
    setEmployeeDrawerMode(null);
    setSelectedEmployeeId(null);
    setCurrentPayroll(null);
    setCopiedPayroll(null);
  };

  const handlePaymentSuccessCallback = useCallback(
    (payrolls: PayrollView[]) => {
      const payrollIdFromPath = location.pathname.match(/\/payrolls\/(pay_[a-zA-Z0-9]+)/)?.[1];
      const status = searchParams.get('status');
      const bulkPaymentId = searchParams.get('bulkPaymentId');

      if (payrollIdFromPath && status === 'success' && bulkPaymentId && payrolls) {
        const payroll = payrolls.find((p) => p.id === payrollIdFromPath);
        if (payroll) {
          setCurrentPayroll(payroll);
          setPayrollAction('PAYROLL_PAYMENT_SUCCESS');

          // Clean up URL parameters after triggering the success modal
          const newPath = location.pathname.replace(/\/payments$/, '');
          navigate(newPath, { replace: true });
        }
      }
    },
    [location.pathname, searchParams, navigate],
  );

  return {
    payrollAction,
    updatePayrollAction,
    clearPayrollState,
    isEmployeeDrawerOpen,
    employeeDrawerMode,
    selectedEmployeeId,
    openEmployeeDrawer,
    closeEmployeeDrawer,
    selectedRowIds,
    setSelectedRowIds,
    currentPayroll,
    setCurrentPayroll,
    viewExistingPayroll,
    handlePaymentSuccessCallback,
    copiedPayroll,
    setCopiedPayroll,
  };
};
